import { useCustomMutation, useList } from '@refinedev/core'
import {
  Alert,
  Button,
  Card,
  DatePicker,
  Form,
  Input,
  Modal,
  Select,
  Space,
  Spin,
  Table,
  Tooltip,
  Typography
} from 'antd'
import {
  CopyOutlined,
  ExclamationCircleFilled,
  PlusOutlined,
  ReloadOutlined,
  RetweetOutlined,
  SearchOutlined,
  StopOutlined,
  UnlockOutlined
} from '@ant-design/icons'
import { useState } from 'react'

import { useMessage } from '@/providers/message-provider'

const { Text } = Typography
const { TextArea } = Input

/**
 * 项目级序列号列表（F7.1）+ 发卡 / 换机 / 吊销操作（F7.2-F7.5）
 *
 * 顶部筛选：status / orderNo
 * 顶部操作按钮组：发卡 / 换机重绑 / 按订单号吊销
 * 行内操作：按 ID 吊销 / 强制解绑（均带 reason 输入）
 * 发卡成功后弹窗展示返回的 serial + 复制按钮
 *
 * 对应文档：direct-issue-design.md §3
 */

/** 序列号状态 */
type LicenseStatus = 'unused' | 'active' | 'revoked'

/** 序列号实体 */
interface License {
  id: string
  serial: string
  status: LicenseStatus
  sku: string
  boundDeviceId: string | null
  boundMachineCode: string | null
  orderNo: string
  createdAt: string
  activatedAt: string | null
  revokedAt: string | null
  revokedReason: string | null
  expiresAt: string | null
  unbindCount: number
  maxUnbind: number
  notes: string | null
}

/** 发卡表单（直接发卡，无需 machineCode） */
interface IssueForm {
  orderNo: string
  sku: string
  expiresAt?: string
  notes?: string
}

/**
 * 换机重绑表单（重构后：仅凭订单号解绑，买家在新机器重新激活时自动绑定）
 *
 * 重构前：需要 newMachineCode，后端查找已注册设备并切换绑定
 * 重构后：仅凭 orderNo 解绑，买家拿原序列号在新机器 activate 时自动绑定新设备
 */
interface RebindForm {
  orderNo: string
  reason?: string
}

/** 按订单号吊销表单 */
interface RevokeByOrderForm {
  orderNo: string
  reason: string
}

/** 行内确认操作类型 */
type InlineAction = 'revoke' | 'unbind'

const monoStyle: React.CSSProperties = {
  fontFamily: 'JetBrains Mono, monospace',
  fontSize: 12
}

/** 序列号状态徽章 */
function StatusBadge({ status }: { status: LicenseStatus }) {
  const config: Record<LicenseStatus, { color: string; bg: string; label: string }> = {
    active: { color: '#10B981', bg: 'rgba(16, 185, 129, 0.1)', label: '活跃' },
    unused: { color: '#F59E0B', bg: 'rgba(245, 158, 11, 0.1)', label: '未使用' },
    revoked: { color: '#F43F5E', bg: 'rgba(244, 63, 94, 0.1)', label: '已吊销' }
  }
  const c = config[status]
  return (
    <span
      style={{
        color: c.color,
        background: c.bg,
        border: `1px solid ${c.color}33`,
        padding: '2px 8px',
        borderRadius: 4,
        fontSize: 12,
        fontWeight: 500
      }}
    >
      {c.label}
    </span>
  )
}

/** 格式化时间 */
function fmt(iso?: string | null): string {
  if (!iso) return '-'
  try {
    return new Date(iso).toLocaleString('zh-CN')
  } catch {
    return iso
  }
}

/** 常用 SKU 选项 */
const SKU_OPTIONS = [
  { value: 'lifetime-1device', label: 'lifetime-1device（终身 1 设备）' },
  { value: '1y-1device', label: '1y-1device（1 年 1 设备）' },
  { value: '1y-2device', label: '1y-2device（1 年 2 设备）' },
  { value: 'lifetime-3device', label: 'lifetime-3device（终身 3 设备）' }
]

/**
 * 状态筛选持久化（localStorage）
 *
 * 设计：状态筛选作为"第一级筛选"，需跨会话/跨登录态保留用户偏好。
 * 使用 localStorage 而非 sessionStorage：退出登录/重启后台后再回来仍保持。
 * 失败时静默降级（隐私模式下 localStorage 不可用，不影响功能）。
 */
const STATUS_FILTER_STORAGE_KEY = 'license-list:status-filter'

function loadStoredStatusFilter(): LicenseStatus | undefined {
  try {
    const v = localStorage.getItem(STATUS_FILTER_STORAGE_KEY)
    if (v === 'unused' || v === 'active' || v === 'revoked') return v
    return undefined
  } catch {
    return undefined
  }
}

function saveStoredStatusFilter(status: LicenseStatus | undefined): void {
  try {
    if (status) localStorage.setItem(STATUS_FILTER_STORAGE_KEY, status)
    else localStorage.removeItem(STATUS_FILTER_STORAGE_KEY)
  } catch {
    // 隐私模式或 storage 被禁用时静默降级
  }
}

export function LicensesList({ projectCode }: { projectCode: string }) {
  const message = useMessage()
  // ── 列表查询状态 ────────────────────────────────────
  // 状态筛选：实时触发 + 持久化（从 localStorage 读取初始值）
  // 订单号搜索：保留 tempOrder + 点搜索应用（文本输入不适合实时筛选）
  const [filters, setFilters] = useState<{
    status?: LicenseStatus
    orderNo?: string
  }>(() => ({ status: loadStoredStatusFilter() }))
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(10)

  // 临时筛选值（仅订单号需要点搜索应用）
  const [tempOrder, setTempOrder] = useState('')

  const listFilters = [
    ...(filters.status ? [{ field: 'status', operator: 'eq' as const, value: filters.status }] : []),
    ...(filters.orderNo
      ? [{ field: 'orderNo', operator: 'eq' as const, value: filters.orderNo }]
      : [])
  ]

  const { data, isLoading, isError, refetch } = useList<License>({
    resource: 'project-licenses',
    meta: { projectCode },
    pagination: { current: page, pageSize },
    filters: listFilters
  })

  const licenses = data?.data ?? []
  const total = data?.total ?? 0

  // ── 操作弹窗状态 ────────────────────────────────────
  const [issueOpen, setIssueOpen] = useState(false)
  const [rebindOpen, setRebindOpen] = useState(false)
  const [revokeByOrderOpen, setRevokeByOrderOpen] = useState(false)

  // 行内操作确认弹窗
  const [inlineAction, setInlineAction] = useState<{
    type: InlineAction
    license: License
  } | null>(null)
  const [inlineReason, setInlineReason] = useState('')

  // 发卡成功 serial 弹窗
  const [serialResult, setSerialResult] = useState<string | null>(null)

  // 换机重绑成功 serial 弹窗（解绑后展示原序列号，方便管理员复制给买家重新激活）
  const [rebindResult, setRebindResult] = useState<{ serial: string; remaining?: number } | null>(null)

  // ── 表单实例 ────────────────────────────────────────
  const [issueForm] = Form.useForm<IssueForm>()
  const [rebindForm] = Form.useForm<RebindForm>()
  const [revokeByOrderForm] = Form.useForm<RevokeByOrderForm>()

  // ── 自定义 mutation ────────────────────────────────
  const { mutate: customMutate, isLoading: mutating } = useCustomMutation()

  /** 通用提交错误处理 */
  const onError = (err: unknown) => {
    const msg = (err as { message?: string })?.message ?? '操作失败'
    message.error(msg)
  }

  // ── 顶部筛选 ────────────────────────────────────────
  /**
   * 状态筛选变化：实时应用 + 持久化
   * 设计：状态是第一级筛选，选择即生效，无需点搜索按钮
   */
  const handleStatusChange = (value: LicenseStatus | undefined) => {
    setFilters((prev) => ({ ...prev, status: value }))
    saveStoredStatusFilter(value)
    setPage(1)
  }

  /**
   * 订单号搜索：点搜索按钮应用
   * 设计：文本输入不适合实时筛选（每次按键都触发 API 浪费），保留点击应用模式
   */
  const handleSearch = () => {
    setFilters((prev) => ({ ...prev, orderNo: tempOrder.trim() || undefined }))
    setPage(1)
  }

  /**
   * 重置：仅清空订单号搜索，保留状态筛选
   * 设计：状态是"第一级筛选"不应被重置；订单号是"次要筛选"可以被重置
   */
  const handleResetFilters = () => {
    setTempOrder('')
    setFilters((prev) => ({ status: prev.status }))
    setPage(1)
  }

  // ── 直接发卡 ────────────────────────────────────────
  const handleIssue = (values: IssueForm) => {
    const payload: Record<string, unknown> = {
      orderNo: values.orderNo,
      sku: values.sku
    }
    if (values.expiresAt) payload.expiresAt = values.expiresAt
    if (values.notes) payload.notes = values.notes

    customMutate(
      {
        url: `/api/admin/${projectCode}/licenses/issue`,
        method: 'post',
        values: payload,
        meta: { projectCode }
      },
      {
        onSuccess: ({ data }) => {
          const resp = data as { serial?: string; license?: { serial?: string } }
          const serial = resp?.serial ?? resp?.license?.serial ?? ''
          message.success('发卡成功')
          setIssueOpen(false)
          issueForm.resetFields()
          if (serial) setSerialResult(serial)
          refetch()
        },
        onError
      }
    )
  }

  // ── 换机重绑（重构后：仅凭订单号解绑，买家在新机器重新激活时自动绑定）──
  const handleRebind = (values: RebindForm) => {
    const payload: Record<string, unknown> = {
      orderNo: values.orderNo
    }
    if (values.reason) payload.reason = values.reason

    customMutate(
      {
        url: `/api/admin/${projectCode}/licenses/rebind`,
        method: 'post',
        values: payload,
        meta: { projectCode }
      },
      {
        onSuccess: ({ data }) => {
          const resp = data as { serial?: string; remaining?: number }
          setRebindOpen(false)
          rebindForm.resetFields()
          refetch()
          // 解绑成功后，展示原序列号弹窗，方便管理员复制给买家重新激活
          if (resp?.serial) {
            setRebindResult({ serial: resp.serial, remaining: resp.remaining })
          } else {
            message.success('换机重绑成功，请告知买家用原序列号在新机器重新激活')
          }
        },
        onError
      }
    )
  }

  // ── 按订单号吊销 ────────────────────────────────────
  const handleRevokeByOrder = (values: RevokeByOrderForm) => {
    customMutate(
      {
        url: `/api/admin/${projectCode}/licenses/revoke-by-order`,
        method: 'post',
        values: { orderNo: values.orderNo, reason: values.reason },
        meta: { projectCode }
      },
      {
        onSuccess: () => {
          message.success('按订单号吊销成功')
          setRevokeByOrderOpen(false)
          revokeByOrderForm.resetFields()
          refetch()
        },
        onError
      }
    )
  }

  // ── 行内操作：按 ID 吊销 / 强制解绑 ────────────────
  const openInlineAction = (type: InlineAction, license: License) => {
    setInlineAction({ type, license })
    setInlineReason('')
  }

  const handleInlineConfirm = () => {
    if (!inlineAction) return
    const { type, license } = inlineAction

    // 吊销操作 reason 必填（后端 revokeByIdSchema.reason 为 z.string().min(1)）
    // 强制解绑 reason 可选（后端 adminUnbindSchema.reason 为 z.string().max(255).optional()）
    if (type === 'revoke' && !inlineReason.trim()) {
      message.warning('请输入吊销原因')
      return
    }

    const url =
      type === 'revoke'
        ? `/api/admin/${projectCode}/licenses/${license.id}/revoke`
        : `/api/admin/${projectCode}/licenses/${license.id}/unbind`

    customMutate(
      {
        url,
        method: 'post',
        values: { reason: inlineReason || undefined },
        meta: { projectCode }
      },
      {
        onSuccess: () => {
          message.success(type === 'revoke' ? '吊销成功' : '强制解绑成功')
          setInlineAction(null)
          setInlineReason('')
          refetch()
        },
        onError
      }
    )
  }

  // ── 复制序列号 ──────────────────────────────────────
  const copySerial = (s: string) => {
    navigator.clipboard.writeText(s)
    message.success('已复制序列号')
  }

  if (isError) {
    return (
      <Card>
        <div style={{ textAlign: 'center', padding: 32 }}>
          <Text type="secondary">加载失败</Text>
          <br />
          <Button icon={<ReloadOutlined />} onClick={() => refetch()} style={{ marginTop: 12 }}>
            重试
          </Button>
        </div>
      </Card>
    )
  }

  return (
    <div>
      {/* 顶部筛选 + 操作区 */}
      <Card size="small" style={{ marginBottom: 16 }}>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            gap: 12,
            flexWrap: 'wrap'
          }}
        >
          <Space wrap>
            <Select
              allowClear
              placeholder="状态筛选"
              value={filters.status}
              onChange={(v) => handleStatusChange(v as LicenseStatus | undefined)}
              style={{ width: 140 }}
              options={[
                { value: 'unused', label: '未使用' },
                { value: 'active', label: '活跃' },
                { value: 'revoked', label: '已吊销' }
              ]}
            />
            <Input
              allowClear
              placeholder="订单号"
              value={tempOrder}
              onChange={(e) => setTempOrder(e.target.value)}
              onPressEnter={handleSearch}
              style={{ width: 220 }}
              prefix={<SearchOutlined style={{ color: 'var(--ant-color-text-tertiary, #94A3B8)' }} />}
            />
            <Button type="primary" onClick={handleSearch}>
              搜索
            </Button>
            <Button onClick={handleResetFilters}>重置</Button>
            <Button icon={<ReloadOutlined />} onClick={() => refetch()}>
              刷新
            </Button>
          </Space>

          <Space>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => setIssueOpen(true)}
            >
              发卡
            </Button>
            <Button icon={<RetweetOutlined />} onClick={() => setRebindOpen(true)}>
              换机重绑
            </Button>
            <Button danger icon={<StopOutlined />} onClick={() => setRevokeByOrderOpen(true)}>
              按订单号吊销
            </Button>
          </Space>
        </div>
      </Card>

      {/* 序列号表格 */}
      <Card size="small">
        <Spin spinning={isLoading}>
          <Table<License>
            rowKey="id"
            dataSource={licenses}
            size="small"
            scroll={{ x: 'max-content' }}
            pagination={{
              current: page,
              pageSize,
              total,
              showSizeChanger: true,
              showTotal: (t) => `共 ${t} 条`,
              onChange: (p, ps) => {
                setPage(p)
                setPageSize(ps)
              }
            }}
            columns={[
              {
                title: 'Serial',
                dataIndex: 'serial',
                width: 240,
                render: (v: string) => (
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                    <code style={monoStyle}>{v}</code>
                    <Tooltip title="复制序列号">
                      <CopyOutlined
                        style={{ cursor: 'pointer', opacity: 0.5, fontSize: 12 }}
                        onClick={() => copySerial(v)}
                      />
                    </Tooltip>
                  </span>
                )
              },
              {
                title: '状态',
                dataIndex: 'status',
                width: 100,
                render: (v: LicenseStatus) => <StatusBadge status={v} />
              },
              {
                title: 'SKU',
                dataIndex: 'sku',
                width: 160,
                render: (v: string) => <code style={monoStyle}>{v}</code>
              },
              {
                title: '已绑机器码',
                dataIndex: 'boundMachineCode',
                width: 220,
                render: (v: string | null, record: License) => {
                  if (record.status === 'unused') {
                    return <Text type="secondary">未激活</Text>
                  }
                  return v ? <code style={monoStyle}>{v}</code> : <Text type="secondary">-</Text>
                }
              },
              {
                title: '订单号',
                dataIndex: 'orderNo',
                width: 160,
                render: (v: string | null) =>
                  v ? <code style={monoStyle}>{v}</code> : <Text type="secondary">-</Text>
              },
              {
                title: '解绑次数',
                key: 'unbindCount',
                width: 100,
                render: (_: unknown, r: License) => (
                  <span className="tabular-nums" style={{ fontSize: 12 }}>
                    {r.unbindCount} / {r.maxUnbind}
                  </span>
                )
              },
              {
                title: '创建时间',
                dataIndex: 'createdAt',
                width: 170,
                render: (v: string) => (
                  <span className="tabular-nums" style={{ fontSize: 12 }}>
                    {fmt(v)}
                  </span>
                )
              },
              {
                title: '操作',
                key: 'op',
                width: 200,
                fixed: 'right',
                render: (_: unknown, record: License) => (
                  <Space size="small">
                    <Button
                      type="link"
                      size="small"
                      danger
                      icon={<StopOutlined />}
                      disabled={record.status === 'revoked'}
                      onClick={() => openInlineAction('revoke', record)}
                      style={{ padding: 0 }}
                    >
                      吊销
                    </Button>
                    <Button
                      type="link"
                      size="small"
                      icon={<UnlockOutlined />}
                      disabled={record.status !== 'active'}
                      onClick={() => openInlineAction('unbind', record)}
                      style={{ padding: 0 }}
                    >
                      强制解绑
                    </Button>
                  </Space>
                )
              }
            ]}
          />
        </Spin>
      </Card>

      {/* 发卡 Modal */}
      <Modal
        open={issueOpen}
        title="发卡"
        width={560}
        onCancel={() => setIssueOpen(false)}
        footer={null}
        maskClosable={false}
      >
        <Alert
          type="info"
          showIcon
          message="为订单生成未绑定序列号，买家激活时自动绑定设备"
          style={{ marginBottom: 16 }}
        />
        <Form<IssueForm>
          form={issueForm}
          layout="vertical"
          onFinish={handleIssue}
          initialValues={{ sku: 'lifetime-1device' }}
        >
          <Form.Item
            name="orderNo"
            label="订单号"
            rules={[{ required: true, message: '请输入订单号' }]}
          >
            <Input placeholder="订单号，便于后续查询与吊销（一单一码）" />
          </Form.Item>
          <Form.Item
            name="sku"
            label="SKU"
            rules={[{ required: true, message: '请选择 SKU' }]}
          >
            <Select options={SKU_OPTIONS} />
          </Form.Item>
          <Form.Item name="expiresAt" label="过期时间（可选）">
            <DatePicker
              showTime
              style={{ width: '100%' }}
              onChange={(_, ds) => {
                issueForm.setFieldValue('expiresAt', ds as string)
              }}
            />
          </Form.Item>
          <Form.Item name="notes" label="备注（可选）">
            <TextArea rows={2} placeholder="备注信息" maxLength={200} showCount />
          </Form.Item>
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
            <Button onClick={() => setIssueOpen(false)}>取消</Button>
            <Button type="primary" htmlType="submit" loading={mutating}>
              确认发卡
            </Button>
          </div>
        </Form>
      </Modal>

      {/* 换机重绑 Modal（重构后：仅凭订单号解绑，买家在新机器重新激活时自动绑定） */}
      <Modal
        open={rebindOpen}
        title="换机重绑"
        width={520}
        onCancel={() => setRebindOpen(false)}
        footer={null}
        maskClosable={false}
      >
        <Alert
          type="warning"
          showIcon
          message="按订单号解绑原机器，买家用原序列号在新机器重新激活即可自动绑定"
          description="解绑后序列号状态恢复为「未使用」，买家拿原序列号在新机器首次激活时，后端会自动注册并绑定新设备。unbind_count 会 +1（年度上限内防滥用）。"
          style={{ marginBottom: 16 }}
        />
        <Form<RebindForm> form={rebindForm} layout="vertical" onFinish={handleRebind}>
          <Form.Item
            name="orderNo"
            label="订单号"
            rules={[{ required: true, message: '请输入订单号' }]}
          >
            <Input placeholder="原订单号" />
          </Form.Item>
          <Form.Item name="reason" label="换机原因（可选）">
            <TextArea rows={2} placeholder="换机原因，便于审计" maxLength={200} showCount />
          </Form.Item>
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
            <Button onClick={() => setRebindOpen(false)}>取消</Button>
            <Button type="primary" htmlType="submit" loading={mutating}>
              确认解绑
            </Button>
          </div>
        </Form>
      </Modal>

      {/* 按订单号吊销 Modal */}
      <Modal
        open={revokeByOrderOpen}
        title="按订单号吊销"
        width={520}
        onCancel={() => setRevokeByOrderOpen(false)}
        footer={null}
        maskClosable={false}
      >
        <Alert
          type="error"
          showIcon
          message="高危操作：将吊销该订单号对应的序列号，不可恢复"
          style={{ marginBottom: 16 }}
        />
        <Form<RevokeByOrderForm> form={revokeByOrderForm} layout="vertical" onFinish={handleRevokeByOrder}>
          <Form.Item
            name="orderNo"
            label="订单号"
            rules={[{ required: true, message: '请输入订单号' }]}
          >
            <Input placeholder="订单号" />
          </Form.Item>
          <Form.Item
            name="reason"
            label="吊销原因"
            rules={[{ required: true, message: '请输入吊销原因' }]}
          >
            <TextArea rows={2} placeholder="吊销原因，便于审计" maxLength={200} showCount />
          </Form.Item>
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
            <Button onClick={() => setRevokeByOrderOpen(false)}>取消</Button>
            <Button type="primary" danger htmlType="submit" loading={mutating}>
              确认吊销
            </Button>
          </div>
        </Form>
      </Modal>

      {/* 行内操作确认弹窗（吊销 / 强制解绑） */}
      <Modal
        open={!!inlineAction}
        title={
          inlineAction?.type === 'revoke' ? (
            <Space>
              <ExclamationCircleFilled style={{ color: '#F43F5E' }} />
              <span>确认吊销该序列号</span>
            </Space>
          ) : (
            <Space>
              <ExclamationCircleFilled style={{ color: '#F59E0B' }} />
              <span>确认强制解绑该序列号</span>
            </Space>
          )
        }
        onCancel={() => setInlineAction(null)}
        onOk={handleInlineConfirm}
        confirmLoading={mutating}
        okText="确认"
        cancelText="取消"
        okButtonProps={{ danger: inlineAction?.type === 'revoke' }}
      >
        {inlineAction && (
          <div>
            <Alert
              type={inlineAction.type === 'revoke' ? 'error' : 'warning'}
              showIcon
              message={
                inlineAction.type === 'revoke'
                  ? '吊销后该序列号立即失效，不可恢复'
                  : '解绑后该序列号可重新绑定到任意设备'
              }
              style={{ marginBottom: 12 }}
            />
            <div style={{ marginBottom: 8 }}>
              <Text type="secondary" style={{ fontSize: 12 }}>
                Serial
              </Text>
              <div>
                <code style={monoStyle}>{inlineAction.license.serial}</code>
              </div>
            </div>
            <div style={{ marginBottom: 8 }}>
              <Text type="secondary" style={{ fontSize: 12 }}>
                订单号
              </Text>
              <div>
                <code style={monoStyle}>
                  {inlineAction.license.orderNo ?? '-'}
                </code>
              </div>
            </div>
            <div style={{ marginBottom: 4 }}>
              <Text type="secondary" style={{ fontSize: 12 }}>
                {inlineAction.type === 'revoke' ? (
                  <>
                    原因 <span style={{ color: '#F43F5E' }}>*</span>
                  </>
                ) : (
                  '原因（可选）'
                )}
              </Text>
            </div>
            <TextArea
              rows={2}
              value={inlineReason}
              onChange={(e) => setInlineReason(e.target.value)}
              placeholder={
                inlineAction.type === 'revoke'
                  ? '请输入吊销原因（必填，便于审计）'
                  : '操作原因，便于审计'
              }
              maxLength={200}
              showCount
            />
          </div>
        )}
      </Modal>

      {/* 发卡成功 serial 弹窗 */}
      <Modal
        open={!!serialResult}
        title="发卡成功"
        onCancel={() => setSerialResult(null)}
        footer={[
          <Button key="copy" icon={<CopyOutlined />} onClick={() => serialResult && copySerial(serialResult)}>
            复制序列号
          </Button>,
          <Button key="close" type="primary" onClick={() => setSerialResult(null)}>
            关闭
          </Button>
        ]}
        maskClosable={false}
      >
        {serialResult && (
          <div>
            <Alert
              type="success"
              showIcon
              message="序列号已生成，请复制后交付给客户"
              style={{ marginBottom: 16 }}
            />
            <Text type="secondary" style={{ fontSize: 12 }}>
              Serial
            </Text>
            <pre
              style={{
                fontFamily: 'JetBrains Mono, monospace',
                fontSize: 14,
                padding: 12,
                background: 'var(--ant-color-fill-tertiary, #F1F5F9)',
                borderRadius: 6,
                margin: '4px 0 0',
                wordBreak: 'break-all',
                whiteSpace: 'pre-wrap'
              }}
            >
              {serialResult}
            </pre>
          </div>
        )}
      </Modal>

      {/* 换机重绑成功 serial 弹窗（解绑后展示原序列号，方便管理员复制给买家重新激活） */}
      <Modal
        open={!!rebindResult}
        title="换机重绑成功"
        onCancel={() => setRebindResult(null)}
        footer={[
          <Button
            key="copy"
            icon={<CopyOutlined />}
            onClick={() => rebindResult && copySerial(rebindResult.serial)}
          >
            复制序列号
          </Button>,
          <Button key="close" type="primary" onClick={() => setRebindResult(null)}>
            关闭
          </Button>
        ]}
        maskClosable={false}
      >
        {rebindResult && (
          <div>
            <Alert
              type="success"
              showIcon
              message="原机器已解绑，请将原序列号发送给买家"
              description={`买家用此序列号在新机器首次激活时，后端会自动注册并绑定新设备。${
                rebindResult.remaining !== undefined
                  ? `本年度剩余换机次数：${rebindResult.remaining} 次。`
                  : ''
              }`}
              style={{ marginBottom: 16 }}
            />
            <Text type="secondary" style={{ fontSize: 12 }}>
              原序列号 Serial
            </Text>
            <pre
              style={{
                fontFamily: 'JetBrains Mono, monospace',
                fontSize: 14,
                padding: 12,
                background: 'var(--ant-color-fill-tertiary, #F1F5F9)',
                borderRadius: 6,
                margin: '4px 0 0',
                wordBreak: 'break-all',
                whiteSpace: 'pre-wrap'
              }}
            >
              {rebindResult.serial}
            </pre>
          </div>
        )}
      </Modal>
    </div>
  )
}
